import { api } from './client'

/** Papéis do membro no comitê — espelham o CHECK de gmud_approvals. */
export type ApproverRole =
  | 'technical' | 'business' | 'security' | 'cab_member' | 'manager'

export const APPROVER_ROLE_LABELS: Record<ApproverRole, string> = {
  manager: 'Gestor (veto)',
  technical: 'Técnico',
  business: 'Negócio',
  security: 'Segurança',
  cab_member: 'Membro do CCM',
}

export interface CommitteeMember {
  user_id: number
  approver_role: ApproverRole
  full_name: string | null
  email: string | null
  is_active: number | boolean
}

export interface CommitteeSummary {
  id: number
  name: string
  description: string | null
  cab_quorum_required: number
  is_active: number | boolean
  member_count: number
  org_count: number
}

export interface Committee extends Omit<CommitteeSummary, 'member_count' | 'org_count'> {
  members: CommitteeMember[]
  organization_ids: number[]
}

export interface OrgSemComite {
  id: number
  name: string
  slug: string
  tenant_count: number
}

/** Tenant alcançado por mais de um comitê, por estar em mais de uma org. */
export interface TenantAmbiguo {
  tenant_id: number
  committee_names: string[]
}

/** Tenant que não pertence a organização nenhuma — fica fora de qualquer
 *  controle de mudança e não aparecia em lugar nenhum antes de 08/08. */
export interface TenantSemOrganizacao {
  tenant_id: number
  name: string
  environment: string | null
}

/** Integrante que aprova no papel e nao alcanca o tenant: esta no comite da org
 *  mas nao tem o tenant atribuido. Aprovaria e tomaria 403. */
export interface MembroSemAlcance {
  committee_id: number
  committee_name: string
  user_id: number
  full_name: string | null
  email: string | null
  tenants_fora: number[]
}

export interface Coverage {
  /** Organizações que exigem controle e não têm comitê ativo. Desde 08/08 isto
   *  não é mais "pendência de cadastro": é o que está SEM controle de mudança. */
  orgs_sem_comite: OrgSemComite[]
  /** Organizações isentas que TÊM comitê — o único caso em que a isenção
   *  explícita ainda decide algo. */
  orgs_isentas_com_comite?: OrgSemComite[]
  tenants_sem_organizacao?: TenantSemOrganizacao[]
  tenants_ambiguos: TenantAmbiguo[]
  /** Integrante de comitê ativo que aprova no papel e não alcança o tenant.
   *  A D3 decidiu não conceder tenant por participação em comitê; este
   *  diagnóstico é o que impede o caso de virar 403 silencioso na hora de
   *  aprovar. */
  membros_sem_alcance?: MembroSemAlcance[]
}

export const ccmApi = {
  list: (includeInactive = false) =>
    api.get<{ items: CommitteeSummary[] }>(
      `/v1/ccm/committees?include_inactive=${includeInactive}`),

  get: (id: number) => api.get<Committee>(`/v1/ccm/committees/${id}`),

  create: (body: { name: string; description?: string; cab_quorum_required: number }) =>
    api.post<Committee>('/v1/ccm/committees', body),

  update: (id: number, body: Partial<{ name: string; description: string; cab_quorum_required: number }>) =>
    api.patch<Committee>(`/v1/ccm/committees/${id}`, body),

  deactivate: (id: number) => api.delete(`/v1/ccm/committees/${id}`),

  /** Estado FINAL desejado, não delta — o backend reconcilia. */
  setMembers: (id: number, members: Array<{ user_id: number; approver_role: ApproverRole }>) =>
    api.put<{ added: number[]; removed: number[] }>(
      `/v1/ccm/committees/${id}/members`, { members }),

  /** Estado FINAL desejado, não delta. */
  setOrganizations: (id: number, organizationIds: number[]) =>
    api.put<{ added: number[]; removed: number[] }>(
      `/v1/ccm/committees/${id}/organizations`, { organization_ids: organizationIds }),

  coverage: () => api.get<Coverage>('/v1/ccm/coverage'),

  /**
   * Comitê que atende este tenant, com os membros. `null` quando o tenant não
   * tem organização, a organização não tem comitê ou o comitê está inativo —
   * os três casos caem no mesmo lugar de propósito: sem validação de aprovador.
   */
  forTenant: (tenantId: number) =>
    api.get<TenantCommittee | null>(`/v1/tenants/${tenantId}/gmud/committee`),
}

export interface TenantCommittee {
  id: number
  name: string
  description: string | null
  cab_quorum_required: number
  is_active: number | boolean
  members: CommitteeMember[]
}
